import { NostoPlacementsChangedEvent, ProductSwappedEvent } from '@theme/events';
import { normalizeSectionId, sectionRenderer } from '@theme/section-renderer';

/**
 * "Compléter votre look" lives in its own Shopify section (see sections/product-cross-sell.liquid),
 * so the phase 3 AJAX color swap — which morphs only the main `product` section — never touches it.
 * This element re-renders itself through the Section Rendering API whenever a sibling color is
 * swapped in, since the sibling's `akeneo.pdt_x_sell` list can differ from the current product's.
 */
class ProductCrossSellRefresh extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }
    document.addEventListener(ProductSwappedEvent.eventName, this.#handleSwap, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /** @returns {string} */
  get #sectionId() {
    const id = this.dataset.sectionId;
    return id ? normalizeSectionId(id) : '';
  }

  /** @param {Event} event */
  #handleSwap = (event) => {
    if (!(event instanceof ProductSwappedEvent)) return;

    const sectionId = this.#sectionId;
    if (!sectionId) return;

    let url;
    try {
      url = new URL(event.detail.productUrl, window.location.origin);
    } catch {
      return;
    }

    // `renderSection` resolves after the morph, so anything announced in `then` sees the new DOM.
    // The sibling may land on the Nosto fallback branch (no Akeneo cross-sell), whose
    // `.nosto_element` comes back empty from the server — Nosto has to be asked to fill it again,
    // and only now, once the container it must target actually exists.
    void sectionRenderer.renderSection(sectionId, { cache: false, mode: 'full', url }).then(() => {
      document.dispatchEvent(new NostoPlacementsChangedEvent({ productId: event.detail.productId }));
    });
  };
}

if (!customElements.get('product-cross-sell-refresh')) {
  customElements.define('product-cross-sell-refresh', ProductCrossSellRefresh);
}
